import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as maintenanceActions from './maintenance.actions';
import { PagedResponse } from "@models/response.model";
import { Recurrence } from '@app/shared/models/recurrence.model';
import { Activity } from '@app/shared/models/activity.model';
import { ActivityTask } from '@models/activity-task.model';
import { MaintenancePlan } from '@app/shared/models/maintenancePlan.model';
import { UserRole } from "@models/role.model";

export const maintenanceFeatureKey = 'maintenance';

export interface MaintenanceState {
    recurrences: PagedResponse<Recurrence>;
    activities: PagedResponse<Activity>;
    tasks: PagedResponse<ActivityTask>;
    plans: PagedResponse<MaintenancePlan>;
    activity: Activity;
    task: ActivityTask;
    plan: MaintenancePlan;
    roles: PagedResponse<UserRole>;
    planTools: any[]
    createdRecurrence: Recurrence;
    createdActivity: Activity;
    createdTask: ActivityTask;
    createdPlan: MaintenancePlan;
    updatedRecurrence: Recurrence;
    updatedActivity: Activity;
    updatedTask: ActivityTask;
    updatedPlan: MaintenancePlan;
    deletedRecurrence: Recurrence;
    workingInstructionFiles: any[];
    spareInstructionFiles: any[];
    deletedTaskId: number;
    deletedActivityId: number;
    deletedPlanId: number;
    isLoading: boolean;
    isLoadingPlans: boolean;
    isLoadingTools: boolean;
    isLoadingActivities: boolean;
    isLoadingRecurrences: boolean;
    isLoadingFiles: boolean;
    isLoadingTasks: boolean;
    error: any;
}

export const initialMaintenanceState: MaintenanceState = {
    recurrences: new PagedResponse<Recurrence>(),
    activities: new PagedResponse<Activity>(),
    tasks: new PagedResponse<ActivityTask>(),
    plans: new PagedResponse<MaintenancePlan>(),
    roles: new PagedResponse<UserRole>(),
    activity: null,
    task: null,
    plan: null,
    planTools: null,
    createdRecurrence: null,
    createdActivity: null,
    createdTask: null,
    createdPlan: null,
    updatedRecurrence: null,
    updatedActivity: null,
    updatedTask: null,
    updatedPlan: null,
    workingInstructionFiles: null,
    spareInstructionFiles: null,
    deletedRecurrence: null,
    deletedTaskId: 0,
    deletedActivityId: 0,
    deletedPlanId: 0,
    isLoading: false,
    isLoadingPlans: false,
    isLoadingTools: false,
    isLoadingActivities: false,
    isLoadingRecurrences: false,
    isLoadingFiles: false,
    isLoadingTasks: false,
    error: null
}

export function reducer(state = initialMaintenanceState, action: maintenanceActions.Action) {
    switch (action.type) {

        // Add Recurrence
        case maintenanceActions.ADD_RECURRENCE:
            return {
                ...state,
                createdRecurrence: null,
                isLoading: true
            };
        case maintenanceActions.ADD_RECURRENCE_SUCCESS:
            const recurrencesAfterAdded = {
                items: state.recurrences.items.concat(action.payload),
                total: state.recurrences.total + 1
            };

            return {
                ...state,
                createdRecurrence: action.payload,
                recurrences: recurrencesAfterAdded,
                isLoading: false
            };
        case maintenanceActions.ADD_RECURRENCE_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load Recurrences
        case maintenanceActions.LOAD_RECURRENCES:
            return {
                ...state,
                recurrences: new PagedResponse<Recurrence>(),
                isLoadingRecurrences: true
            };
        case maintenanceActions.LOAD_RECURRENCES_SUCCESS:
            const recurrencesAfterLoad = {
                items: action.payload,
                total: action.payload.length
            }
            return {
                ...state,
                recurrences: recurrencesAfterLoad,
                isLoadingRecurrences: false
            };
        case maintenanceActions.LOAD_RECURRENCES_FAILED:
            return {
                ...state,
                isLoadingRecurrences: false,
                error: action.payload
            };

        // Update recurrence
        case maintenanceActions.UPDATE_RECURRENCE:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.UPDATE_RECURRENCE_SUCCESS:
            const recurrencesAfterUpdated = {
                items: state.recurrences.items.map(item => item.id === action.payload.id ? action.payload : item),
            };
            return {
                ...state,
                recurrences: recurrencesAfterUpdated,
                updatedRecurrence: action.payload,
                isLoading: false
            };
        case maintenanceActions.UPDATE_RECURRENCE_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Delete recurrence
        case maintenanceActions.DELETE_RECURRENCE:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.DELETE_RECURRENCE_SUCCESS:
            const recurrencesAfterDeleted = {
                items: state.recurrences.items.filter(item => item.id !== action.payload.id),
                total: state.recurrences.total - 1
            };
            return {
                ...state,
                deletedRecurrence: action.payload,
                recurrences: recurrencesAfterDeleted,
                isLoading: false
            };
        case maintenanceActions.DELETE_RECURRENCE_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Add Activity
        case maintenanceActions.ADD_ACTIVITY:
            return {
                ...state,
                createdActivty: null,
                isLoading: true
            };
        case maintenanceActions.ADD_ACTIVITY_SUCCESS:
            const activitiesAfterAdded = {
                items: state.activities.items.concat(action.payload),
                total: state.activities.total + 1
            };
            return {
                ...state,
                createdActivity: action.payload,
                activities: activitiesAfterAdded,
                isLoading: false
            };

        case maintenanceActions.ADD_ACTIVITY_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load Activities
        case maintenanceActions.LOAD_ACTIVITIES:
            return {
                ...state,
                activities: new PagedResponse<Activity>(),
                isLoadingActivities: true
            };
        case maintenanceActions.LOAD_ACTIVITIES_SUCCESS:
            const activitiesAfterLoad = {
                items: action.payload,
                total: action.payload.length
            }
            return {
                ...state,
                activities: activitiesAfterLoad,
                isLoadingActivities: false
            };
        case maintenanceActions.LOAD_ACTIVITIES_FAILED:
            return {
                ...state,
                isLoadingActivities: false,
                error: action.payload
            };
        // Update Activities
        case maintenanceActions.UPDATE_ACTIVITIES:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.UPDATE_ACTIVITIES_SUCCESS:
            const activitiesAfterUpdate = {
                items: action.payload,
                total: action.payload.length
            }
            return {
                ...state,
                activitiesAfterUpdate,
                isLoading: false
            };
        case maintenanceActions.UPDATE_ACTIVITIES_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };
        // Load Activity
        case maintenanceActions.LOAD_ACTIVITY:
            return {
                ...state,
                activity: null,
                isLoading: true
            };
        case maintenanceActions.LOAD_ACTIVITY_SUCCESS:
            return {
                ...state,
                activity: action.payload,
                isLoading: false
            };
        case maintenanceActions.LOAD_ACTIVITY_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Update activity
        case maintenanceActions.UPDATE_ACTIVITY:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.UPDATE_ACTIVITY_SUCCESS:
            const activitiesAfterUpdated = {
                items: state.activities.items.map(item => item.id === action.payload.id ? action.payload : item),
            };
            return {
                ...state,
                activities: activitiesAfterUpdated,
                updatedActivity: action.payload,
                isLoading: false
            };
        case maintenanceActions.UPDATE_ACTIVITY_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Delete activity
        case maintenanceActions.DELETE_ACTIVITY:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.DELETE_ACTIVITY_SUCCESS:
            const activitiesAfterDeleted = {
                items: state.activities.items.filter(item => item.id !== action.payload),
                total: state.activities.total - 1
            };
            return {
                ...state,
                activities: activitiesAfterDeleted,
                deletedActivityId: action.payload,
                isLoading: false
            };
        case maintenanceActions.DELETE_ACTIVITY_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Add Task
        case maintenanceActions.ADD_TASK:
            return {
                ...state,
                createdTask: null,
                isLoading: true
            };
        case maintenanceActions.ADD_TASK_SUCCESS:
            const tasksAfterAdded = {
                items: state.tasks.items.concat(action.payload),
                total: state.tasks.total + 1
            };

            return {
                ...state,
                createdTask: action.payload,
                tasks: tasksAfterAdded,
                isLoading: false
            };
        case maintenanceActions.ADD_TASK_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load Tasks
        case maintenanceActions.LOAD_TASK:
            return {
                ...state,
                task: null,
                isLoading: true
            };
        case maintenanceActions.LOAD_TASK_SUCCESS:
            return {
                ...state,
                task: action.payload,
                isLoading: false
            };
        case maintenanceActions.LOAD_TASK_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load Tasks
        case maintenanceActions.LOAD_TASKS:
            return {
                ...state,
                tasks: new PagedResponse<ActivityTask>(),
                isLoadingTasks: true
            };
        case maintenanceActions.LOAD_TASKS_SUCCESS:
            const tasksAfterLoad = {
                items: action.payload,
                total: action.payload.length
            }
            return {
                ...state,
                tasks: tasksAfterLoad,
                isLoadingTasks: false
            };
        case maintenanceActions.LOAD_TASKS_FAILED:
            return {
                ...state,
                isLoadingTasks: false,
                error: action.payload
            };

        // Update task
        case maintenanceActions.UPDATE_TASK:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.UPDATE_TASK_SUCCESS:
            const tasksAfterUpdated = {
                items: state.tasks.items.map(item => item.id === action.payload.id ? action.payload : item),
            };
            return {
                ...state,
                tasks: tasksAfterUpdated,
                updatedTask: action.payload,
                isLoading: false
            };
        case maintenanceActions.UPDATE_TASK_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };
        // Update Task Photos
        case maintenanceActions.UPDATE_TASK_PHOTOS:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.UPDATE_TASK_PHOTOS_SUCCESS:
            const tasksAfterPhotosUpdated = {
                items: state.tasks.items.map(item => item.id === action.payload.id ? action.payload : item),
            };

            return {
                ...state,
                updatedTask: action.payload,
                tasks: tasksAfterPhotosUpdated,
                isLoading: false
            };
        case maintenanceActions.UPDATE_TASK_PHOTOS_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Delete task
        case maintenanceActions.DELETE_TASK:
            return {
                ...state,
                task: null,
                isLoading: true
            };
        case maintenanceActions.DELETE_TASK_SUCCESS:
            const tasksAfterDeleted = {
                items: state.tasks.items.filter(item => item.id !== action.payload),
                total: state.tasks.total - 1
            };
            return {
                ...state,
                deletedTaskId: action.payload,
                tasks: tasksAfterDeleted,
                isLoading: false
            };
        case maintenanceActions.DELETE_TASK_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };
        // Add Plan
        case maintenanceActions.ADD_PLAN:
            return {
                ...state,
                createdPlan: null,
                isLoadingPlans: true,
                isLoading: true
            };
        case maintenanceActions.ADD_PLAN_SUCCESS:
            const plansAfterAdded = {
                items: state.plans.items.concat(action.payload),
                total: state.plans.total + 1
            };
            return {
                ...state,
                createdPlan: action.payload,
                plans: plansAfterAdded,
                isLoadingPlans: false,
                isLoading: false
            };
        case maintenanceActions.ADD_PLAN_FAILED:
            return {
                ...state,
                isLoadingPlans: false,
                isLoading: false,
                error: action.payload
            };

        // Load Maintenance Plans
        case maintenanceActions.LOAD_PLANS:
            return {
                ...state,
                plans: new PagedResponse<MaintenancePlan>(),
                isLoadingPlans: true
            };
        case maintenanceActions.LOAD_PLANS_SUCCESS:
            const loadedPlans = {
                items: action.firstPage ? action.payload.items : state.plans.items.concat(action.payload.items),
                total: action.payload.total
            }

            return {
                ...state,
                plans: loadedPlans,
                plan: null,
                createdPlan: null,
                updatedPlan: null,
                isLoadingPlans: false
            };
        case maintenanceActions.LOAD_PLANS_FAILED:
            return {
                ...state,
                isLoadingPlans: false,
                error: action.payload
            };

        // Load Maintenance Plan
        case maintenanceActions.LOAD_PLAN:
            return {
                ...state,
                plan: null,
                isLoading: true
            };
        case maintenanceActions.LOAD_PLAN_SUCCESS:
            return {
                ...state,
                plan: action.payload,
                isLoading: false
            };
        case maintenanceActions.LOAD_PLAN_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load Maintenance Plan Tools
        case maintenanceActions.LOAD_PLAN_TOOLS:
            return {
                ...state,
                planTools: null,
                isLoadingTools: true
            };
        case maintenanceActions.LOAD_PLAN_TOOLS_SUCCESS:
            return {
                ...state,
                planTools: action.payload,
                isLoadingTools: false
            };
        case maintenanceActions.LOAD_PLAN_TOOLS_FAILED:
            return {
                ...state,
                isLoadingTools: false,
                error: action.payload
            };

        // Update plan
        case maintenanceActions.UPDATE_PLAN:
            return {
                ...state,
                updatedPlan: null,
                isLoading: true
            };
        case maintenanceActions.UPDATE_PLAN_SUCCESS:
            const plansAfterUpdated = {
                items: state.plans.items.map(item => item.id === action.payload.id ? action.payload : item),
            };
            return {
                ...state,
                plans: plansAfterUpdated,
                updatedPlan: action.payload,
                isLoading: false
            };
        case maintenanceActions.UPDATE_PLAN_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Delete plan
        case maintenanceActions.DELETE_MAINTENANCE_PLAN:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.DELETE_MAINTENANCE_PLAN_SUCCESS:
            const plansAfterDeleted = {
                items: state.plans.items.filter(item => item.id !== action.payload),
                total: state.plans.total - 1
            };
            return {
                ...state,
                plans: plansAfterDeleted,
                deletedPlanId: action.payload,
                isLoading: false
            };
        case maintenanceActions.DELETE_MAINTENANCE_PLAN_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load Roles
        case maintenanceActions.LOAD_ROLES:
            return {
                ...state,
                isLoading: true
            };
        case maintenanceActions.LOAD_ROLES_SUCCESS:
            return {
                ...state,
                roles: action.payload,
                isLoading: false
            };
        case maintenanceActions.LOAD_ROLES_FAILED:
            return {
                ...state,
                isLoading: false
            };


        // Load Task Work Instruction Files
        case maintenanceActions.LOAD_TASK_WORK_INSTRUCTION_FILES:
            return {
                ...state,
                isLoadingFiles: true
            };
        case maintenanceActions.LOAD_TASK_WORK_INSTRUCTION_FILES_SUCCESS:
            return {
                ...state,
                workingInstructionFiles: action.payload,
                isLoadingFiles: false
            };
        case maintenanceActions.LOAD_TASK_WORK_INSTRUCTION_FILES_FAILED:
            return {
                ...state,
                isLoadingFiles: false
            };


        // Load Task Spare Instruction Files
        case maintenanceActions.LOAD_TASK_SPARE_INSTRUCTION_FILES:
            return {
                ...state,
                isLoadingFiles: true
            };
        case maintenanceActions.LOAD_TASK_SPARE_INSTRUCTION_FILES_SUCCESS:
            return {
                ...state,
                spareInstructionFiles: action.payload,
                isLoadingFiles: false
            };
        case maintenanceActions.LOAD_TASK_SPARE_INSTRUCTION_FILES_FAILED:
            return {
                ...state,
                isLoadingFiles: false
            };

        default:
            return state;
    }
}

export const getMaintenanceState = createFeatureSelector<MaintenanceState>(maintenanceFeatureKey);
export const getRecurrences = createSelector(getMaintenanceState, (state: MaintenanceState) => state.recurrences);
export const getActivities = createSelector(getMaintenanceState, (state: MaintenanceState) => state.activities);
export const getTasks = createSelector(getMaintenanceState, (state: MaintenanceState) => state.tasks);
export const getPlans = createSelector(getMaintenanceState, (state: MaintenanceState) => state.plans);
export const getActivity = createSelector(getMaintenanceState, (state: MaintenanceState) => state.activity);
export const getTask = createSelector(getMaintenanceState, (state: MaintenanceState) => state.task);
export const getPlan = createSelector(getMaintenanceState, (state: MaintenanceState) => state.plan);
export const getPlanTools = createSelector(getMaintenanceState, (state: MaintenanceState) => state.planTools);
export const getCreatedRecurrence = createSelector(getMaintenanceState, (state: MaintenanceState) => state.createdRecurrence);
export const getCreatedActivity = createSelector(getMaintenanceState, (state: MaintenanceState) => state.createdActivity);
export const getCreatedPlan = createSelector(getMaintenanceState, (state: MaintenanceState) => state.createdPlan);
export const getUpdatedActivity = createSelector(getMaintenanceState, (state: MaintenanceState) => state.updatedActivity);
export const getCreatedTask = createSelector(getMaintenanceState, (state: MaintenanceState) => state.createdTask);
export const getUpdatedTask = createSelector(getMaintenanceState, (state: MaintenanceState) => state.updatedTask);
export const getUpdatedPlan = createSelector(getMaintenanceState, (state: MaintenanceState) => state.updatedPlan);
export const getDeletedRecurrence = createSelector(getMaintenanceState, (state: MaintenanceState) => state.deletedRecurrence);
export const getDeletedActivityId = createSelector(getMaintenanceState, (state: MaintenanceState) => state.deletedActivityId);
export const getRoles = createSelector(getMaintenanceState, (state: MaintenanceState) => state.roles);
export const getDeletedPlanId = createSelector(getMaintenanceState, (state: MaintenanceState) => state.deletedPlanId);
export const isLoading = createSelector(getMaintenanceState, (state: MaintenanceState) => state.isLoading);
export const isLoadingPlans = createSelector(getMaintenanceState, (state: MaintenanceState) => state.isLoadingPlans);
export const isLoadingTasks = createSelector(getMaintenanceState, (state: MaintenanceState) => state.isLoadingTasks);
export const getError = createSelector(getMaintenanceState, (state: MaintenanceState) => state.error);
export const isLoadingFiles = createSelector(getMaintenanceState, (state: MaintenanceState) => state.isLoadingFiles);
export const workInstructionFiles = createSelector(getMaintenanceState, (state: MaintenanceState) => state.workingInstructionFiles);
export const spareInstructionFiles = createSelector(getMaintenanceState, (state: MaintenanceState) => state.spareInstructionFiles);
